import { constructPhotoUrl, checkImageLoads } from './photoUrls';

const bustUrl = (url, version) => {
  if (!url || !version) return url;
  return `${url.split('?')[0]}?v=${version}`;
};

const versionKey = (pid) => `photo-v-${pid}`;

const getStoredVersion = (pid) => {
  try {
    const v = sessionStorage.getItem(versionKey(pid));
    return v ? Number(v) : null;
  } catch {
    return null;
  }
};

const setStoredVersion = (pid, version) => {
  try {
    if (version) sessionStorage.setItem(versionKey(pid), String(version));
    else sessionStorage.removeItem(versionKey(pid));
  } catch {
    // ignore storage errors
  }
};

const getVersion = (pid) => photoCache.versions.get(pid) ?? getStoredVersion(pid);

// Photo cache service
const photoCache = {
  cache: new Map(),
  loading: new Set(),
  loadedPids: new Set(),
  versions: new Map(),
  listeners: new Map(),

  loadPhoto: async (pid) => {
    if (!pid) return null;
    if (photoCache.cache.has(pid)) return photoCache.cache.get(pid);
    if (photoCache.loading.has(pid)) return null;
    if (photoCache.loadedPids.has(pid)) return null;

    photoCache.loading.add(pid);
    try {
      const url = constructPhotoUrl(pid);
      const loaded = await checkImageLoads(url);
      const photoUrl = loaded ? url : null;
      if (photoUrl) {
        photoCache.cache.set(pid, photoCache.getBustedUrl(pid, photoUrl));
      }
      photoCache.loadedPids.add(pid);
      return photoUrl;
    } catch (error) {
      photoCache.loadedPids.add(pid);
      return null;
    } finally {
      photoCache.loading.delete(pid);
    }
  },

  getPhoto: (pid) => photoCache.cache.get(pid),

  getBustedUrl: (pid, url) => bustUrl(url, getVersion(pid)),

  setPhoto: (pid, url) => {
    photoCache.cache.set(pid, url);
    photoCache.loadedPids.add(pid);
  },

  removePhoto: (pid) => {
    photoCache.cache.delete(pid);
    photoCache.loadedPids.delete(pid);
    photoCache.versions.delete(pid);
    setStoredVersion(pid, null);
  },

  // Single entry point for "the photo behind this parcel_id changed" — bumps the cache-busting
  // version and notifies every subscriber (e.g. every PhotoCell/PropertyRecordCard showing this
  // parcel). Pass url=null to record a deletion. Always use this over setPhoto/removePhoto
  // directly when the change should be visible to other mounted components.
  invalidate: (pid, url = null) => {
    let version = null;
    if (url) {
      version = Date.now();
      photoCache.versions.set(pid, version);
      setStoredVersion(pid, version);
      photoCache.cache.set(pid, bustUrl(url, version));
      photoCache.loadedPids.add(pid);
    } else {
      photoCache.versions.delete(pid);
      setStoredVersion(pid, null);
      photoCache.cache.delete(pid);
      photoCache.loadedPids.delete(pid);
    }
    photoCache.listeners.get(pid)?.forEach((fn) => fn(version));
  },

  subscribe: (pid, callback) => {
    if (!photoCache.listeners.has(pid)) {
      photoCache.listeners.set(pid, new Set());
    }
    photoCache.listeners.get(pid).add(callback);
    return () => photoCache.listeners.get(pid)?.delete(callback);
  },

  isLoaded: (pid) => photoCache.cache.has(pid),
  isLoading: (pid) => photoCache.loading.has(pid),
  hasAttemptedLoad: (pid) => photoCache.loadedPids.has(pid),
};

export const preloadPhotos = (coordinates) => {
  coordinates.forEach((coord) => {
    if (coord.parcel_id && !photoCache.hasAttemptedLoad(coord.parcel_id)) {
      photoCache.loadPhoto(coord.parcel_id);
    }
  });
};

export const getPhotoContent = (pid) => {
  if (!pid) {
    return '<div style="display: flex; align-items: center; justify-content: center; color: #666;">No Photo Available</div>';
  }

  if (photoCache.isLoading(pid)) {
    return '<div style="display: flex; align-items: center; justify-content: center; color: #666;">Loading Photo...</div>';
  }

  const photoUrl = photoCache.getPhoto(pid);
  if (!photoUrl) {
    return '<div style="display: flex; align-items: center; justify-content: center; color: #666;">Photo Unavailable</div>';
  }

  return `<img src="${photoUrl}" style="width: 100%; height: 100%; object-fit: cover;" alt="Property Photo" />`;
};

export { photoCache };
export default photoCache;
